"use client";

import { useState } from "react";
import { useTranslation } from "@/lib/i18n/context";

/** YouTube video id for the product demo. Swap here if the video is re-uploaded. */
const VIDEO_ID = "3CEzUKKgCRY";

// Self-contained bilingual copy — parity kept here rather than in the global
// Translations type (this is one localized marketing section). See lang below.
const COPY = {
  sl: {
    eyebrow: "// KAKO DELUJE",
    heading: { a: "Poglej cel dan, ", em: "od jutra do večera", b: "." },
    button: "Poglej demo",
    sub: "120-sekundni ogled.",
  },
  en: {
    eyebrow: "// SEE IT IN ACTION",
    heading: { a: "Watch a day, ", em: "start to finish", b: "." },
    button: "Watch the demo",
    sub: "A 120-second look.",
  },
} as const;

/**
 * Branded, click-to-play product demo. The facade (paper frame + logo mark +
 * terracotta play button) is pure CSS/SVG using brand vars, so it adapts to
 * dark mode and makes ZERO external requests until the user clicks — only then
 * do we mount the YouTube (nocookie) iframe. Frame styling mirrors the hero
 * figure card (HeroSection.tsx).
 */
export function DemoVideoSection() {
  const { lang } = useTranslation();
  const c = COPY[lang === "en" ? "en" : "sl"];
  const [playing, setPlaying] = useState(false);

  // The video audio is English. For Slovenian UI, force YouTube captions on and
  // prefer the Slovenian subtitle track (must exist on the video in YouTube
  // Studio — upload an SL track or publish an auto-translated one).
  const params = new URLSearchParams({
    autoplay: "1",
    rel: "0",
    modestbranding: "1",
    playsinline: "1",
  });
  if (lang === "sl") {
    params.set("cc_load_policy", "1");
    params.set("cc_lang_pref", "sl");
    params.set("hl", "sl");
  }
  const embedSrc = `https://www.youtube-nocookie.com/embed/${VIDEO_ID}?${params.toString()}`;

  return (
    <section id="demo" className="scroll-mt-[72px] px-5 md:px-8 py-14 md:py-20">
      <div className="mx-auto max-w-[960px]">
        <div className="mb-5 md:mb-7 text-center">
          <span className="inline-flex items-center rounded-full border border-[var(--rule)] px-3 py-1 font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--ink-soft)]">
            {c.eyebrow}
          </span>
          <h2
            className="mt-3 font-display text-[clamp(28px,3.4vw,44px)] leading-[1.05] tracking-[-0.02em] text-balance"
            style={{ fontWeight: 350 }}
          >
            {c.heading.a}
            <em className="text-[var(--terracotta)] italic">{c.heading.em}</em>
            {c.heading.b}
          </h2>
        </div>

        <div
          className="relative aspect-video rounded-[18px] md:rounded-[24px] border border-[var(--hair)] overflow-hidden"
          style={{
            background:
              "radial-gradient(120% 80% at 50% 0%, rgba(184,83,26,0.06), transparent 60%), linear-gradient(180deg, var(--paper), var(--paper-2))",
            boxShadow: "0 1px 2px rgba(26,24,21,0.04), 0 24px 60px rgba(26,24,21,0.08)",
          }}
        >
          {playing ? (
            <iframe
              className="absolute inset-0 h-full w-full"
              src={embedSrc}
              title="Journail product demo"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <button
              type="button"
              onClick={() => setPlaying(true)}
              aria-label={c.button}
              className="group absolute inset-0 flex flex-col items-center justify-center gap-4 focus:outline-none"
            >
              {/* Logo mark: ink ring + terracotta core (matches /icon.svg) */}
              <span className="relative h-14 w-14 rounded-full border-[3px] border-[var(--ink)] flex items-center justify-center">
                <span className="absolute inset-[6px] rounded-full bg-[var(--terracotta)]" />
              </span>
              {/* Terracotta play pill */}
              <span className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--terracotta)] text-[var(--terracotta-soft)] font-medium text-[15px] transition group-hover:-translate-y-px group-hover:shadow-[0_8px_20px_rgba(184,83,26,0.28)]">
                <svg width="13" height="15" viewBox="0 0 14 16" fill="currentColor" aria-hidden="true">
                  <path d="M0 1.3v13.4a1 1 0 0 0 1.5.86l11.3-6.7a1 1 0 0 0 0-1.72L1.5.44A1 1 0 0 0 0 1.3Z" />
                </svg>
                {c.button}
              </span>
              <span className="font-display italic text-[15px] text-[var(--ink-soft)]">{c.sub}</span>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
